import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { EMAIL_TEMPLATE_DESIGN_ONLY_TYPES } from "constants/templates";
import type { TemplateSourceType } from "services/suno-comms/types/text-templates.type";
import { TextTemplate } from "services/types/text-templates.type";
import { viteEnv } from "utils/vite-env.util";

import { useAppSelector } from "../../app/hooks";
import { selectCurrentUserToken } from "../../features/auth/auth-slice";
import { withoutBrandingMergeTags } from "../email-branding/email-branding.utils";
import { useEmailBranding } from "../email-branding/use-email-branding.hook";
import TextTemplatesDialogV2, {
  TemplateSubmitData
} from "../word-processor-field/text-templates/text-templates-v2/text-templates-dialog-v2.component";
import { imagesUploadHandler } from "../word-processor-field/word-processor-field-setup.utils";
import type { EmailBuilderMergeTags } from "./email-builder-merge-tags";

type EmailBuilderComponentProps = {
  emailBuilderRef: React.MutableRefObject<UnlayerEditor | null>;
  visible?: boolean;
  initialMessage?: string;
  initialDesign?: UnlayerDesign | null;
  mergeTags?: EmailBuilderMergeTags;
  specialLinks?: UnlayerSpecialLink[];
  setActiveDesign?: (design: UnlayerDesign | null) => void;
  setEditorTabIsDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Callback when design is updated in the builder.
   * Used to detect changes for enabling save buttons.
   */
  onDesignChange?: () => void;
  /**
   * Template types to filter in the templates dialog.
   * When not defined, uses EMAIL_TEMPLATE_DESIGN_ONLY_TYPES.
   */
  templateTypes?: number[];
  /**
   * Whether this instance is active and should respond to global events.
   * When multiple instances are mounted, only the active one responds to
   * events like opening the templates modal.
   */
  isActive?: boolean;
  /**
   * When true, the builder fills its container (width and height 100%).
   * Used for full-screen mode.
   */
  fillContainer?: boolean;
  /**
   * Pixels to subtract from 95vh when computing builder height (default 500).
   * Increase to reduce height in contexts with less surrounding chrome.
   */
  heightOffset?: number;
  /** Backend to read templates from. Omitted ⇒ main backend. */
  templateSource?: TemplateSourceType;
  /** Raise the templates dialog above nested panels (e.g. leads inbox). */
  dialogZIndex?: number;
  /** Called when a template is inserted from the builder templates dialog. */
  onTemplateInserted?: (template: TextTemplate) => void;
  /** Called when the Unlayer editor finishes initializing or re-initializing. */
  onEditorReady?: () => void;
  /** Optional transform applied to designer template JSON before load (e.g. resolve merge tags). */
  transformTemplateDesign?: (design: UnlayerDesign) => UnlayerDesign;
};

export default function EmailBuilderComponent({
  emailBuilderRef,
  visible = true,
  initialDesign,
  setActiveDesign,
  setEditorTabIsDisabled,
  onDesignChange,
  templateTypes = EMAIL_TEMPLATE_DESIGN_ONLY_TYPES,
  isActive = true,
  mergeTags: mergeTagsProp,
  specialLinks,
  fillContainer = false,
  heightOffset = 500,
  templateSource,
  dialogZIndex,
  onTemplateInserted,
  onEditorReady,
  transformTemplateDesign
}: EmailBuilderComponentProps) {
  const token = useAppSelector(selectCurrentUserToken);
  const { isSupported: isBrandingSupported } = useEmailBranding();
  // The branding tags live in the shared sets, so they are dropped here, once, for every Designer
  // surface while email branding is off. Memoized: setMergeTags re-runs on every identity change.
  const mergeTags = useMemo(() => {
    if (isBrandingSupported || !mergeTagsProp) return mergeTagsProp;
    return withoutBrandingMergeTags(mergeTagsProp);
  }, [isBrandingSupported, mergeTagsProp]);
  const [isLoading, setIsLoading] = useState(true);
  const [showTemplatesDialog, setShowTemplatesDialog] = useState(false);

  const containerId = useRef(new Date().getTime().toString()).current;
  const fieldIdRef = useRef<string | undefined>(
    `email-builder-templates-${containerId}`
  );

  // Listen for postMessage from Unlayer iframe to open templates modal.
  // Only respond when this instance is visible and active to prevent multiple modals.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "OPEN_TEMPLATES_MODAL" && isActive && visible) {
        setShowTemplatesDialog(true);
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [isActive, visible]);

  const resolveTemplateDesign = useCallback(
    (design: UnlayerDesign | null | undefined) => {
      if (!design) return design;
      return transformTemplateDesign ? transformTemplateDesign(design) : design;
    },
    [transformTemplateDesign]
  );

  const handleTemplateSubmit = useCallback(
    (values: TemplateSubmitData) => {
      const { template, needReplace } = values;

      if (!template || !emailBuilderRef.current) {
        setShowTemplatesDialog(false);
        return;
      }

      // If template has email_design_json, use it directly
      if (template.email_design_json) {
        const templateDesign = resolveTemplateDesign(
          template.email_design_json
        );

        if (needReplace) {
          emailBuilderRef.current.loadDesign(templateDesign as UnlayerDesign);
        } else {
          // Append: export current design, merge rows, then load
          emailBuilderRef.current.exportHtml(
            (data: { design: UnlayerDesign }) => {
              const currentDesign = data.design;

              if (templateDesign?.body?.rows && currentDesign?.body?.rows) {
                const mergedDesign: UnlayerDesign = {
                  ...currentDesign,
                  body: {
                    ...currentDesign.body,
                    rows: [
                      ...currentDesign.body.rows,
                      ...templateDesign.body.rows
                    ]
                  }
                };
                emailBuilderRef.current?.loadDesign(mergedDesign);
              } else {
                emailBuilderRef.current?.loadDesign(
                  templateDesign as UnlayerDesign
                );
              }
            }
          );
        }
      }
      //  else if (template.text) {
      //   // Convert plain text to Unlayer design structure
      //   const textWithReplacedMergeTags = template.text.replace(
      //     /{{(.*?)}}/g,
      //     "!--SUNO:$1--"
      //   );

      //   const wrappedText = textWithReplacedMergeTags.startsWith("<")
      //     ? textWithReplacedMergeTags
      //     : `<p style="line-height: 140%;">${textWithReplacedMergeTags}</p>`;

      //   const textDesign: UnlayerDesign = {
      //     counters: { u_row: 1, u_column: 1, u_content_text: 1 },
      //     body: {
      //       id: "body-1",
      //       rows: [
      //         {
      //           id: `row-template-${Date.now()}`,
      //           cells: [1],
      //           columns: [
      //             {
      //               id: `col-template-${Date.now()}`,
      //               contents: [
      //                 {
      //                   id: `text-template-${Date.now()}`,
      //                   type: "text",
      //                   values: {
      //                     anchor: "",
      //                     text: stripUnlayerShell(wrappedText),
      //                     _meta: {
      //                       htmlID: "u_content_text_1",
      //                       htmlClassNames: "u_content_text"
      //                     }
      //                   }
      //                 }
      //               ]
      //             }
      //           ]
      //         }
      //       ]
      //     }
      //   };

      //   if (needReplace) {
      //     emailBuilderRef.current.loadDesign(textDesign);
      //   } else {
      //     // Append text design rows to current design
      //     emailBuilderRef.current.exportHtml(
      //       (data: { design: UnlayerDesign }) => {
      //         const currentDesign = data.design;
      //         if (currentDesign?.body?.rows) {
      //           const mergedDesign: UnlayerDesign = {
      //             ...currentDesign,
      //             body: {
      //               ...currentDesign.body,
      //               rows: [...currentDesign.body.rows, ...textDesign.body.rows]
      //             }
      //           };
      //           emailBuilderRef.current?.loadDesign(mergedDesign);
      //         } else {
      //           emailBuilderRef.current?.loadDesign(textDesign);
      //         }
      //       }
      //     );
      //   }
      // }

      onTemplateInserted?.(template);

      setEditorTabIsDisabled?.(true);
      setShowTemplatesDialog(false);
    },
    [
      emailBuilderRef,
      onTemplateInserted,
      resolveTemplateDesign,
      setEditorTabIsDisabled
    ]
  );

  useEffect(() => {
    setIsLoading(true);

    const editor = unlayer.createEditor({
      id: containerId,
      projectId: Number(viteEnv.VITE_UNLAYER_PROJECT_ID),
      displayMode: "email",
      appearance: {
        loader: {
          html: "<div></div>",
          css: ""
        }
      },
      features: {
        audit: true,
        headersAndFooters: true,
        ai: {
          copilot: true,
          magicImage: true,
          smartText: true,
          smartButtons: true,
          smartImageAltText: true,
          smartHeadings: true
        },
        stockImages: true,
        userUploads: {
          enabled: false
        }
      },
      customCSS: [
        `.toolbar-group [role="menu"] [role="menuitem"],
        [role="menu"][aria-labelledby] [role="menuitem"] {
          min-height: 30px !important;
          height: auto !important;
          padding-top: 4px !important;
          padding-bottom: 4px !important;
        }`,
        // The Merge Tags list is long; the editor iframe is often short. Cap the
        // menu height and scroll inside it so its top items aren't clipped off the
        // top of the iframe on small screens.
        `.toolbar-group [role="menu"],
        [role="menu"][aria-labelledby] {
          max-height: 70vh !important;
          overflow-y: auto !important;
        }`
      ],
      customJS: [
        `
  (function() {
    function injectCustomButton() {
      const navTabs = document.querySelector('ul.nav.nav-tabs');

      if (!navTabs) {
        setTimeout(injectCustomButton, 100);
        return;
      }

      if (document.getElementById('custom-templates-button')) {
        return;
      }

      const li = document.createElement('li');
      li.id = 'custom-templates-button';
      li.className = 'nav-item tab-blocks';
      li.style.marginTop = '15px';

      const a = document.createElement('a');
      a.setAttribute('role', 'button');
      a.className = 'nav-link';

      const iconSpan = document.createElement('span');
      iconSpan.className = 'nav-icon';

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('class', 'MuiSvgIcon-root MuiSvgIcon-fontSizeMedium css-1phnduy');
      svg.setAttribute('focusable', 'false');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('width', '20');
      svg.setAttribute('height', '20');
      svg.style.width = '20px';
      svg.style.height = '20px';

      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'm20.41 8.41-4.83-4.83c-.37-.37-.88-.58-1.41-.58H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V9.83c0-.53-.21-1.04-.59-1.42M7 7h7v2H7zm10 10H7v-2h10zm0-4H7v-2h10z');

      svg.appendChild(path);
      iconSpan.appendChild(svg);

      const nameSpan = document.createElement('span');
      nameSpan.className = 'nav-name';
      nameSpan.textContent = 'Templates';

      a.appendChild(iconSpan);
      a.appendChild(nameSpan);

      a.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        (window.top || window.parent).postMessage(
          { type: "OPEN_TEMPLATES_MODAL" },
          "*"
        );
      });

      li.appendChild(a);
      navTabs.insertBefore(li, navTabs.firstChild);
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', injectCustomButton);
    } else {
      injectCustomButton();
    }
  })();
  `,
        // The Merge Tags list is long and the editor iframe is often short, so
        // Unlayer's popper opens the menu with its top edge above the iframe
        // viewport — the top items get clipped and can't be scrolled into view.
        // A CSS max-height alone doesn't help because the clipped region is the
        // menu container itself, not its scroll content. This observer waits for
        // any role="menu" to open and nudges it back inside the viewport, capping
        // its height so it scrolls internally instead of overflowing.
        `
  (function() {
    var MARGIN = 8;

    function clampMenu(menu) {
      var vh = document.documentElement.clientHeight || window.innerHeight;
      menu.style.setProperty('overflow-y', 'auto', 'important');

      var maxH = vh - MARGIN * 2;
      if (menu.offsetHeight > maxH) {
        menu.style.setProperty('max-height', maxH + 'px', 'important');
      }

      var rect = menu.getBoundingClientRect();
      var curTop = parseFloat(window.getComputedStyle(menu).top) || 0;
      if (rect.top < MARGIN) {
        menu.style.setProperty('top', (curTop + (MARGIN - rect.top)) + 'px', 'important');
      } else if (rect.bottom > vh - MARGIN) {
        menu.style.setProperty('top', (curTop - (rect.bottom - (vh - MARGIN))) + 'px', 'important');
      }
    }

    function handle(menu) {
      // Let the popper finish positioning before measuring.
      requestAnimationFrame(function() { clampMenu(menu); });
      setTimeout(function() { clampMenu(menu); }, 60);
    }

    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(m) {
        Array.prototype.forEach.call(m.addedNodes, function(node) {
          if (node.nodeType !== 1) return;
          if (node.matches && node.matches('[role="menu"]')) handle(node);
          if (node.querySelectorAll) {
            Array.prototype.forEach.call(node.querySelectorAll('[role="menu"]'), handle);
          }
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  })();
  `
      ],
      mergeTags,
      specialLinks,
      tools: {
        html: {
          enabled: true
        },
        text: {
          enabled: true
        },
        image: {
          enabled: true,
          properties: {
            src: {
              editor: {
                widgetParams: {
                  placeholderUrl: "IMAGE_PLACEHOLDER_URL"
                }
              }
            }
          }
        },
        button: { enabled: true },
        divider: { enabled: true },
        spacer: { enabled: true },
        heading: {
          enabled: true
        },
        video: { enabled: true },
        social: { enabled: true },
        menu: { enabled: true },
        timer: { enabled: true }
      }
    });

    editor?.registerCallback(
      "image",
      async function (
        file: { attachments: File[] },
        done: (result: { progress?: number; url?: string }) => void
      ) {
        try {
          const actualFile = file.attachments?.[0];

          done({ progress: 10 });

          const blobInfo = {
            blob: () => actualFile,
            base64: () => {
              return new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const result = reader.result as string;
                  resolve(result.split(",")[1]);
                };
                reader.onerror = reject;
                reader.readAsDataURL(actualFile);
              });
            }
          };

          const progress = () => {};

          const url = await imagesUploadHandler({
            token,
            autoUpload: true,
            blobInfo,
            progress,
            maxImageSizeMB: 4
          });

          done({ progress: 100, url });
        } catch (err) {
          const errorMessage =
            err instanceof Error ? err.message : "Failed to upload image";
          console.error("Image upload error:", errorMessage);
          done({ progress: 100 });
        }
      }
    );

    editor?.addEventListener("editor:ready", () => {
      setIsLoading(false);
      onEditorReady?.();
    });

    editor?.addEventListener("design:updated", function (data) {
      if (data.type && data.type !== "design:migrated") {
        setEditorTabIsDisabled?.(true);
        onDesignChange?.();
      }
    });

    emailBuilderRef.current = editor;
    return () => {
      editor?.destroy?.();
      emailBuilderRef.current = null;
    };
    // Recreate the editor when the merge-tag/special-link set changes (e.g. a
    // LaunchDarkly flag flipping the available tags). Recreating rebuilds the editor
    // with the final tags registered and re-runs the design load below, so merge tags
    // tokenize into {{ }} chips deterministically instead of leaving raw
    // `!--SUNO:..--` text. (ENG-1091)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emailBuilderRef,
    token,
    containerId,
    setEditorTabIsDisabled,
    specialLinks
  ]);

  useEffect(() => {
    if (isLoading || !emailBuilderRef.current || !mergeTags) return;
    emailBuilderRef.current.setMergeTags?.(mergeTags);
  }, [emailBuilderRef, isLoading, mergeTags]);

  useEffect(() => {
    if (visible && !isLoading && emailBuilderRef.current) {
      if (initialDesign) {
        emailBuilderRef.current.loadDesign(initialDesign);
        setActiveDesign?.(null);
        setEditorTabIsDisabled?.(true);
        return;
      }

      // NOTE: Loading from initialMessage is disabled to keep Email Builder content independent
      // from the Editor. To re-enable, uncomment the code below and add this ref:
      // const hasInitializedFromMessageRef = useRef(false);
      //
      // if (initialMessage && !hasInitializedFromMessageRef.current) {
      //   hasInitializedFromMessageRef.current = true;
      //
      //   const messageWithReplacedMergeTags = initialMessage.replace(
      //     /{{(.*?)}}/g,
      //     "!--SUNO:$1--"
      //   );
      //
      //   const wrappedMessage = messageWithReplacedMergeTags.startsWith("<")
      //     ? messageWithReplacedMergeTags
      //     : `<p style="line-height: 140%;">${messageWithReplacedMergeTags}</p>`;
      //
      //   emailBuilderRef.current.loadDesign({
      //     counters: {
      //       u_row: 1,
      //       u_column: 1,
      //       u_content_text: 1
      //     },
      //     body: {
      //       id: "body-1",
      //       rows: [
      //         {
      //           id: "row-1",
      //           cells: [1],
      //           columns: [
      //             {
      //               id: "col-1",
      //               contents: [
      //                 {
      //                   id: "text-1",
      //                   type: "text",
      //                   values: {
      //                     anchor: "",
      //                     text: stripUnlayerShell(wrappedMessage),
      //                     _meta: {
      //                       htmlID: "u_content_text_1",
      //                       htmlClassNames: "u_content_text"
      //                     }
      //                   }
      //                 }
      //               ]
      //             }
      //           ]
      //         }
      //       ]
      //     }
      //   });
      // }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, emailBuilderRef, visible, initialDesign, setActiveDesign]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        ...(fillContainer
          ? {
              display: visible ? "flex" : "none",
              flexDirection: "column",
              height: "100%",
              flex: 1,
              minHeight: 0
            }
          : {
              display: visible ? "block" : "none"
            })
      }}
    >
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: 10,
            gap: 2
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading email builder...
          </Typography>
        </Box>
      )}
      <div
        id={containerId}
        style={{
          boxSizing: "border-box",
          maxWidth: "100%",
          ...(fillContainer
            ? {
                flex: 1,
                minHeight: 0,
                width: "100%",
                border: "1px solid #e0e0e0",
                borderRadius: 4,
                overflow: "hidden"
              }
            : {
                width: "100%",
                height: `calc(95vh - ${heightOffset}px)`,
                minHeight: `400px`,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                overflow: "hidden"
              })
        }}
      ></div>

      <TextTemplatesDialogV2
        fieldIdRef={fieldIdRef}
        show={showTemplatesDialog}
        onClose={() => setShowTemplatesDialog(false)}
        templateTypes={templateTypes}
        onSubmit={handleTemplateSubmit}
        disableComposeEmail
        templateSource={templateSource}
        dialogZIndex={dialogZIndex}
      />
    </Box>
  );
}
